import React, {
  useEffect, useState, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import classes from './physicianAdd.module.scss';
import InputField from '../../../_common/Form/TextFields/InputField';
import Button from '../../../_common/Form/Buttons/Button';
import Select from '../../../_common/Form/Selects/Select/Select';
import { specOptions, stateOptions } from '../../../../utils/data/data';
import { getSelectRepListRequest } from '../../../../store/actions/users';
import utils from '../../../../utils/utils';
import AccountHelpers from '../../../../utils/accountHelpers';
import api from '../../../../api/api';
import { getPhysiciansRequest } from '../../../../store/actions/physicians';
import Loader from '../../../_common/Loader/Loader';
import { getHealthRequest } from '../../../../store/actions/healths';
import Checkbox from '../../../_common/Form/Checkbox/Checkbox';

const inputNamePhysician = [
  {
    name: 'First Name',
    path: 'firstName',
  },
  {
    name: 'Last Name',
    path: 'lastName',
  },
  {
    name: 'Group',
    path: 'group',
  },
  {
    name: 'Rep',
    path: 'rep',
  },
  {
    name: 'Hub',
    path: 'hubId',
  },
  {
    name: 'Specialization',
    path: 'specialization',
  },
  {
    name: 'DEA',
    path: 'DEA',
  },
  {
    name: 'NPI',
    path: 'NPI',
  },
  {
    name: 'Phone',
    path: 'phone',
  },
  {
    name: 'Fax',
    path: 'fax',
  },
  {
    name: 'Email',
    path: 'email',
  },
  {
    name: 'Contact',
    path: 'contact',
  },
];

const inputAddressPhysician = [
  {
    name: 'Street',
    path: 'addressStreet',
  },
  {
    name: 'City',
    path: 'addressCity',
  },
  {
    name: 'State',
    path: 'addressState',
  },
  {
    name: 'Zip Code',
    path: 'addressZipCode',
  },
];

const inputNameHealth = [
  {
    name: 'Health Plan name',
    path: 'name',
  },
  {
    name: 'Hub',
    path: 'hubId',
  },
  {
    name: 'Rep',
    path: 'rep',
  },
  {
    name: 'Phone',
    path: 'phone',
  },
  {
    name: 'Email',
    path: 'email',
  },
  {
    name: 'Contact',
    path: 'contact',
  },
];

const inputAddressHealth = [
  {
    name: 'Street',
    path: 'addressStreet',
  },
  {
    name: 'City',
    path: 'addressCity',
  },
  {
    name: 'State',
    path: 'addressState',
  },
  {
    name: 'Zip Code',
    path: 'addressZipCode',
  },
];

const limit = 20;

const PhysicianAdd = ({ edit, health }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const params = useParams();
  const physicianId = params?.physicianId;
  const healthId = params?.healthId;

  const canChangePriceAccess = AccountHelpers.isInRole('Admin') || AccountHelpers.isLoggedInAsPriorityPharmacies();

  const dispatch = useDispatch();
  const { selectRepList, pageCount, currentPage } = useSelector((store) => store.users);
  const physiciansList = useSelector((store) => store.physicians.physiciansList);
  const healthList = useSelector((store) => store.healths.healthList);

  const initialData = useCallback(() => {
    let data = {
      addressCity: '',
      addressState: '',
      addressStreet: '',
      addressZipCode: '',
      contact: '',
      email: '',
      repId: '',
      hubId: !health && AccountHelpers.getUser().role === 'Health'
        ? (AccountHelpers.getUser().healthUser?.[0]?.hubId || '') : '',
      phone: '',
      rep: '',
    };

    if (health) {
      data.name = '';
      data.canSeePrice = false;
      data.canSeeProfit = false;
    } else {
      data.DEA = '';
      data.NPI = '';
      data.fax = '';
      data.firstName = '';
      data.group = '';
      data.lastName = '';
      data.physicianWarning = '';
      data.specialization = '';
    }

    if (edit && (healthList.length || physiciansList.length)) {
      if (health) {
        data = _.forIn(data, (value, key) => {
          if (key === 'rep') {
            data[key] = healthList[0].Representative?.name || '';
          } else {
            data[key] = healthList[0][key] ?? '';
          }
        });
      } else {
        data = _.forIn(data, (value, key) => {
          if (key === 'repId') {
            data[key] = physiciansList[0][key] || null;
          } else {
            data[key] = physiciansList[0][key] || '';
          }
        });
      }
    }

    return data;
  }, [physiciansList, healthList]);

  const [requestData, setRequestData] = useState(initialData());
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [editLoad, setEditLoad] = useState(true);

  const {
    rep, specialization, physicianWarning, addressState, canSeePrice, canSeeProfit,
  } = requestData;

  useEffect(() => {
    dispatch(getSelectRepListRequest({ limit, role: 'Rep' }));

    if (edit) {
      (async () => {
        if (health) {
          await dispatch(getHealthRequest({ healthId, userId: AccountHelpers.getUser().id }));
        } else {
          await dispatch(getPhysiciansRequest({ physicianId, userId: AccountHelpers.getUser().id }));
        }

        setEditLoad(false);
      })();
    }
  }, []);

  useEffect(() => {
    if (edit) {
      (async () => {
        await setRequestData(initialData());
      })();
    }
  }, [physiciansList, healthList]);

  const nextOptionListRequest = async (repPage) => {
    await dispatch(getSelectRepListRequest({ page: repPage, limit, role: 'Rep' }));
  };

  const changePhysician = (path, value) => {
    setErrors((prev) => ({ ...prev, [path]: '' }));

    if (path === 'rep') {
      setRequestData((prev) => ({ ...prev, [path]: value.name || '', repId: value.id || null }));
    } else {
      setRequestData((prev) => ({ ...prev, [path]: value }));
    }
  };

  const onKeyPress = (e, validationType, isPaste) => {
    if (validationType === 'addressZipCode') {
      utils.keyPressOnlyNumber(e, isPaste);
    } else if (validationType === 'phone') {
      utils.keyPressPhoneNumber(e, isPaste);
    } else {
      () => {};
    }
  };

  const onSave = async () => {
    let hasError = false;

    _.map(requestData, (value, key) => {
      const requiredFiled = ['firstName', 'lastName', 'name'];

      if (health) requiredFiled.push('hubId');

      if (requiredFiled.includes(key) && !value) {
        setErrors((prev) => ({ ...prev, [key]: 'Field is required' }));
        hasError = true;
      }
      // else if (value && key === 'email' && !utils.isEmail(value)) {
      //   setErrors((prev) => ({ ...prev, [key]: 'Invalid email' }));
      //   hasError = true;
      // }
    });

    if (!hasError) {
      setLoading(true);

      try {
        if (!edit) {
          const data = utils.deleteEmptyKeys(requestData);

          if (health) {
            delete data.rep;
            await api.addHealth(data);
          } else {
            await api.createPhysician(data);
          }

          navigate(health ? '/health' : '/physicians');
        } else {
          if (health) {
            await api.updateHealth({ ...requestData, id: healthId });
          } else {
            await api.updatePhysician({ ...requestData, id: physicianId });
          }

          navigate(pathname.replace('/edit', ''));
        }
        toast.success(`${health ? 'Health' : 'Physician'} successfully ${!edit ? 'created' : 'updated'}`);
      } catch (e) {
        toast.error('Something went wrong');
        console.warn(e);
      }

      setLoading(false);
    } else {
      toast.error('Please correct all errors');
    }
  };

  const inputName = health ? inputNameHealth : inputNamePhysician;

  const inputAddress = health ? inputAddressHealth : inputAddressPhysician;

  return (
    <div className={classes.wrapper}>
      <h2>
        { edit
          ? health
            ? 'Update Health'
            : 'Update Physician'
          : health ? 'Add Health'
            : ' Add Physician'}
      </h2>

      { edit && editLoad ? <Loader className={classes.edit_loader} />
        : (
          <div>
            <div>
              <div>
                {inputName.map((i) => (

                  i.name === 'Rep'
                    ? (
                      <div className={classes.field_wrapper} key={i}>
                        <Select
                          label={i.name}
                          nextOptionListRequest={nextOptionListRequest}
                          options={selectRepList}
                          totalPage={pageCount}
                          currentPage={currentPage}
                          limit={limit}
                          currentOption={rep}
                          onChange={(option) => changePhysician('rep', option)}
                          optionRenderPath="name"
                        />
                      </div>
                    )
                    : i.name === 'Specialization'
                      ? (
                        <div className={classes.field_wrapper} key={i.name}>
                          <Select
                            label={i.name}
                            options={specOptions}
                            currentOption={specialization}
                            onChange={(option) => changePhysician('specialization', option)}
                          />
                        </div>
                      )
                      : (
                        <div className={classes.field_wrapper} key={i.name}>
                          <InputField
                            label={i.name}
                            value={requestData[i.path]}
                            onChange={(value) => changePhysician(i.path, value)}
                            onKeyPress={(e) => onKeyPress(e, i.path)}
                            onPaste={(e) => onKeyPress(e, i.path, true)}
                            error={errors[i.path] || ''}
                          />
                        </div>
                      )
                ))}

                {health && (
                  <div className={classes.checkbox_wrapper}>
                    <Checkbox
                      label="Can See Price"
                      onChange={(check) => canChangePriceAccess && changePhysician('canSeePrice', check)}
                      checked={!!canSeePrice}
                      disabled={!canChangePriceAccess}
                    />

                    <Checkbox
                      label="Can See Profit"
                      onChange={(check) => canChangePriceAccess && changePhysician('canSeeProfit', check)}
                      checked={!!canSeeProfit}
                      disabled={!canChangePriceAccess}
                    />
                  </div>
                )}
              </div>

              {!health && (
              <div className={classes.field_textarea}>
                <InputField
                  label="Physician Warning"
                  textareaField
                  wrapperClassName={classes.textarea_wrapper}
                  value={physicianWarning}
                  onChange={(value) => changePhysician('physicianWarning', value)}
                />
              </div>
              )}
            </div>

            <div className={classes.field_address}>
              <h2>{health ? 'Health Address' : 'Physician Address'}</h2>
              {inputAddress.map((i) => (
                i.name === 'State'
                  ? (
                    <div className={classes.field_wrapper} key={i.name}>
                      <Select
                        label={i.name}
                        onChange={(option) => changePhysician('addressState', option)}
                        currentOption={addressState}
                        options={stateOptions}
                        zIndex={30}
                      />
                    </div>
                  )

                  : (
                    <div className={classes.field_wrapper} key={i.name}>
                      <InputField
                        label={i.name}
                        value={requestData[i.path]}
                        onChange={(value) => changePhysician(i.path, value)}
                        onKeyPress={(e) => onKeyPress(e, i.path)}
                        onPaste={(e) => onKeyPress(e, i.path, true)}
                      />
                    </div>
                  )
              ))}
            </div>

            <div className={classes.btns_wrapper}>

              <Button
                title={edit ? health ? 'Update health' : 'Update physician' : 'Create physician'}
                className={classes.assign_btn}
                type="button"
                addButton
                reverseColor
                onClick={onSave}
                loading={loading}
              />

              <Button
                title="Cancel"
                type="button"
                clearButton
                onClick={() => navigate(-1)}
              />
            </div>

          </div>
        )}
    </div>
  );
};

PhysicianAdd.propTypes = {
  edit: PropTypes.bool,
  health: PropTypes.bool,
};

PhysicianAdd.defaultProps = {
  edit: false,
  health: false,
};

export default PhysicianAdd;
